import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { ECOSYSTEM_KINDS, kindByPath } from "@/lib/ecosystem";
import EcosystemCard from "@/components/EcosystemCard";
import NewsCard from "@/components/NewsCard";
import JobCard from "@/components/JobCard";
import AlertCard from "@/components/AlertCard";
import InsightCard from "@/components/InsightCard";
import HtmlText from "@/components/HtmlText";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import { buildEcosystemOrgSchema, clampText } from "@/lib/schema";
import { htmlToPlain } from "@/lib/text";
import { Globe, Linkedin, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (!url ? "" : url.startsWith("/api/") ? `${BACKEND}${url}` : url);

// =========================
// Ecosystem unified list (/ecosystem and /ecosystem/{kindPath})
// =========================
const SORT_OPTIONS = [
  { v: "activity", labelEn: "Most active (7d)", labelFr: "Plus actifs (7j)" },
  { v: "recent", labelEn: "Most recent", labelFr: "Plus récents" },
  { v: "oldest", labelEn: "Oldest first", labelFr: "Plus anciens" },
  { v: "az", labelEn: "Name A → Z", labelFr: "Nom A → Z" },
  { v: "za", labelEn: "Name Z → A", labelFr: "Nom Z → A" },
];

export const EcosystemHub = () => {
  const { kindPath } = useParams();
  const { lang } = useLang();
  const initialKind = kindByPath(kindPath || "")?.kind || "all";
  const [kindFilter, setKindFilter] = useState(initialKind);
  const [sort, setSort] = useState("activity");
  const [q, setQ] = useState("");
  const [counts, setCounts] = useState({ vendor: 0, service_provider: 0, expert: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Keep the dropdown in sync with the URL if user navigates via header/footer to /ecosystem/{path}
  useEffect(() => {
    const k = kindByPath(kindPath || "")?.kind || "all";
    setKindFilter(k);
  }, [kindPath]);

  useEffect(() => {
    api.get("/ecosystem/counts", { params: { language: lang } })
      .then((r) => setCounts(r.data))
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    const params = { language: lang, sort, with_activity: true, days: 7 };
    if (kindFilter !== "all") params.kind = kindFilter;
    if (q) params.q = q;
    api.get("/ecosystem", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kindFilter, sort, q, lang]);

  const title = lang === "fr" ? "Écosystème" : "Ecosystem";
  const desc = lang === "fr"
    ? "L'écosystème Supply Chain IA : éditeurs, cabinets et experts qui façonnent la nouvelle chaîne de décision."
    : "The Supply Chain AI ecosystem: vendors, service providers and experts shaping the new decision chain.";
  const pageTitle = `${title} — Dataleo Radar`;

  const kindLabelAll = lang === "fr" ? "Tous les types" : "All types";

  return (
    <div data-testid="ecosystem-hub" className="dl-container py-14">
      <SeoHead
        lang={lang}
        path={kindPath ? `/ecosystem/${kindPath}` : "/ecosystem"}
        title={title + " — Dataleo Radar"}
        bareTitle
        description={desc}
      />
      <div className="border-b border-[#111827] pb-4 mb-6">
        <div className="dl-overline text-[#00A878]">Dataleo</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#111827]">{title}</h1>
        <p className="mt-3 text-[#4B5563] max-w-3xl">{desc}</p>
        <div className="mt-3 font-mono-dl text-[11px] uppercase tracking-wider text-[#9ca3af]">
          {counts.total} {counts.total <= 1 ? (lang === "fr" ? "entrée" : "entry") : (lang === "fr" ? "entrées" : "entries")}
          {" · "}
          {counts.vendor} {lang === "fr" ? "éditeurs" : "vendors"}
          {" · "}
          {counts.service_provider} {lang === "fr" ? "cabinets" : "service providers"}
          {" · "}
          {counts.expert} experts
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <label className="flex flex-col gap-1 text-[12px]">
          <span className="dl-overline">{lang === "fr" ? "Type" : "Type"}</span>
          <select
            data-testid="ecosystem-filter-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827] min-w-[180px]"
          >
            <option value="all">{kindLabelAll} ({counts.total})</option>
            {ECOSYSTEM_KINDS.map((k) => (
              <option key={k.kind} value={k.kind}>
                {(lang === "fr" ? k.labelFr : k.labelEn)} ({counts[k.kind] || 0})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px]">
          <span className="dl-overline">{lang === "fr" ? "Trier par" : "Sort by"}</span>
          <select
            data-testid="ecosystem-filter-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827] min-w-[180px]"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.v} value={s.v}>{lang === "fr" ? s.labelFr : s.labelEn}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] flex-1 min-w-[200px]">
          <span className="dl-overline">{lang === "fr" ? "Rechercher" : "Search"}</span>
          <input
            data-testid="ecosystem-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "fr" ? "Nom, expertise, tags…" : "Name, expertise, tags…"}
            className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
      </div>

      {loading ? (
        <div className="text-[#4B5563]">…</div>
      ) : items.length === 0 ? (
        <div data-testid="ecosystem-empty" className="text-[#4B5563] py-12 text-center">
          {lang === "fr" ? "Aucune entité ne correspond à ce filtre." : "No entries match this filter."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ecosystem-grid">
          {items.map((e, i) => <EcosystemCard key={e.id} entity={e} idx={i} lang={lang} />)}
        </div>
      )}
    </div>
  );
};

// Backwards-compat alias for /ecosystem/{kindPath} — same component, the URL just pre-selects the dropdown.
export const EcosystemList = EcosystemHub;

// =========================
// Ecosystem entity detail (/ecosystem/vendors/o9-solutions)
// =========================
export const EcosystemDetail = () => {
  const { kindPath, slug } = useParams();
  const { lang } = useLang();
  const kind = kindByPath(kindPath);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!kind || !slug) return;
    setData(null); setNotFound(false);
    api.get(`/ecosystem/${kind.kind}/${slug}`, { params: { language: lang } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response && e.response.status === 404) setNotFound(true); });
  }, [kind, slug, lang]);

  if (!kind) return <Navigate to="/ecosystem" replace />;
  if (notFound) return <Navigate to={`/ecosystem/${kind.path}`} replace />;
  if (!data) return <div className="dl-container py-20 text-[#4B5563]">…</div>;

  const e = data.entity;
  const related = data.related || {};
  const topItems = Array.isArray(related.top) ? related.top : [];
  const totalRelated = topItems.length || Object.values(related).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
  const canonical = `${BACKEND || ""}/ecosystem/${kind.path}/${e.slug}`;
  const overline = lang === "fr" ? kind.overlineFr : kind.overlineEn;
  const pageTitle = `${e.name} — ${overline} · Dataleo Radar`;
  const metaDesc = htmlToPlain(e.description, 180) || `${overline} — ${e.name}`;

  return (
    <div data-testid={`ecosystem-entity-${e.slug}`} className="dl-container py-14">
      <SeoHead
        lang={lang}
        path={`/ecosystem/${kind.path}/${e.slug}`}
        title={`${e.name} — ${overline}`}
        description={clampText(metaDesc, 160)}
        type="profile"
        image={e.logo_url}
        jsonLd={buildEcosystemOrgSchema({
          baseUrl: SITE_URL,
          entity: e,
          path: `/ecosystem/${kind.path}/${e.slug}`,
        })}
      />

      <Link to={`/ecosystem/${kind.path}`} className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#111827]">
        <ArrowLeft size={14} /> {lang === "fr" ? kind.labelFr : kind.labelEn}
      </Link>
      {e.translated_id && (
        <div data-testid="entity-language-badge" className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono-dl uppercase tracking-wider text-[#4B5563]">
          <span className="px-1.5 py-0.5 bg-[#00A878] text-white">{lang.toUpperCase()}</span>
          <span>·</span>
          <span>{lang === "fr" ? "Disponible aussi en anglais — basculez via le drapeau" : "Also available in French — switch via the flag"}</span>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-[160px_1fr] gap-5 sm:gap-6 items-start border-b border-[#111827] pb-8 mb-8">
        <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-[160px] md:h-[160px] eco-logo-frame border border-[#E5E7EB] overflow-hidden">
          {e.logo_url ? (
            <img src={resolveImg(e.logo_url)} alt={e.name} className="max-h-[70%] max-w-[70%]" />
          ) : (
            <div className="font-display text-5xl text-[#9ca3af]">{(e.name || "?").slice(0, 1).toUpperCase()}</div>
          )}
        </div>
        <div>
          <div className="dl-overline text-[#00A878]">{overline}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#111827]">{e.name}</h1>
          {e.expertise && e.expertise.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {e.expertise.map((ex) => (
                <span key={ex} className="text-[11px] font-mono-dl uppercase tracking-wider px-2 py-0.5 bg-[#F3F4F6] text-[#111827] border border-[#E5E7EB]">{ex}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-5 text-[13px]">
            {e.website && (
              <a data-testid="entity-website" href={e.website} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-[#111827] hover:text-[#00A878]">
                <Globe size={14} /> {lang === "fr" ? "Site web" : "Website"}
              </a>
            )}
            {e.linkedin && (
              <a data-testid="entity-linkedin" href={e.linkedin} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-[#111827] hover:text-[#00A878]">
                <Linkedin size={14} /> LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {e.description && (
        <section className="mb-10">
          <div className="dl-overline text-[#00A878] mb-3">{lang === "fr" ? "Présentation" : "About"}</div>
          <div className="prose prose-neutral max-w-none">
            <HtmlText html={e.description} />
          </div>
        </section>
      )}

      {e.dataleo_perspective && (
        <section className="mb-10 bg-[#111827] text-white p-6 border-l-4 border-[#00A878]">
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878] mb-2">Dataleo perspective</div>
          <div className="prose prose-invert max-w-none text-[#E5E7EB]">
            <HtmlText html={e.dataleo_perspective} />
          </div>
        </section>
      )}

      {/* Related content — flat top-12 mixed list, sorted by keyword overlap then recency */}
      <section className="mt-10">
        <div className="border-b border-[#111827] pb-2 mb-6">
          <div className="dl-overline text-[#00A878]">{lang === "fr" ? "Contenu associé" : "Related content"}</div>
          <h2 className="font-display text-2xl text-[#111827] mt-1">
            {lang === "fr" ? "Autour de" : "Around"} {e.name} <span className="text-[#9ca3af] text-base font-mono-dl">({totalRelated})</span>
          </h2>
        </div>

        {totalRelated === 0 && (
          <div data-testid="entity-related-empty" className="text-[#4B5563] py-12 text-center">
            {lang === "fr"
              ? `Pas encore de contenu lié — renseignez des mots-clés dans related_keywords de ${e.name} (et dans linked_keywords des contenus correspondants) pour alimenter cette section.`
              : `No related content yet — fill in ${e.name}'s related_keywords (and the matching content's linked_keywords) to populate this section.`}
          </div>
        )}

        {topItems.length > 0 && (
          <ul data-testid="entity-related-top" className="divide-y divide-[#E5E7EB] border-t border-b border-[#E5E7EB]">
            {topItems.map((it) => {
              const t = it._type;
              const href =
                t === "tutorials" ? `/tutorials/${it.id}` :
                t === "insights"  ? `/insights/${it.id}`  :
                t === "news"      ? `/news/${it.id}`      :
                t === "alerts"    ? `/alerts/${it.id}`    :
                t === "jobs"      ? `/jobs/${it.id}`      : "#";
              const dt = (it.published_at || "").slice(0, 10);
              return (
                <li key={it.id} data-testid={`related-item-${it.id}`} className="py-3 flex items-baseline gap-3">
                  <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878] w-20 shrink-0">{t}</span>
                  <Link to={href} className="flex-1 text-[#111827] hover:text-[#00A878] underline-offset-4 hover:underline truncate">
                    {it.title || it.name}
                  </Link>
                  <span className="hidden sm:inline font-mono-dl text-[10px] text-[#9ca3af]">{dt}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Tags */}
      {e.tags && e.tags.length > 0 && (
        <div className="mt-10 pt-6 border-t border-[#E5E7EB] flex flex-wrap gap-2">
          {e.tags.map((tg) => (
            <Link key={tg} to={`/search?q=${encodeURIComponent(tg)}`} className="text-[11px] px-2 py-0.5 bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB] hover:border-[#111827]">
              #{tg}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
